import React, { FC, memo, useMemo } from 'react';
import { WarehouseForBooking } from 'entities/Warehouse';
import { GeolocationCoords } from 'app/types/common';
import { useMapProvider } from 'app/providers/MapProvider';
import { Map } from 'features/Map';
import { YandexMap } from 'features/YandexMap';
import { useGeolocation } from 'shared/utils/hooks/useGeolocation';

interface UnifiedWarehouseMapProps {
  warehouseInfo: WarehouseForBooking;
  mapContainerClassName?: string;
  zoom?: number;
  mapId?: string;
  withMarkerPopups?: boolean;
}

export const UnifiedWarehouseMap: FC<UnifiedWarehouseMapProps> = memo((props) => {
  const {
    warehouseInfo,
    mapContainerClassName = 'absolute inset-0 w-full h-full',
    zoom = 15,
    mapId = 'unifiedWarehouseMap',
    withMarkerPopups = false,
  } = props;

  const { mapProvider } = useMapProvider();
  const userCoords = useGeolocation();

  // Provide fallback coordinates to prevent undefined errors
  const mapCenter = useMemo((): GeolocationCoords => {
    if (warehouseInfo?.latitude && warehouseInfo?.longitude) {
      return { lat: warehouseInfo.latitude, lng: warehouseInfo.longitude };
    }
    if (userCoords?.lat && userCoords?.lng) {
      return userCoords;
    }
    // Default to Moscow if no coordinates available
    return { lat: 55.7558, lng: 37.6176 };
  }, [warehouseInfo, userCoords]);

  if (mapProvider === 'yandex') {
    const yandexMarkers = warehouseInfo
      ? [
          {
            id: warehouseInfo.warehouseId,
            position: { lat: warehouseInfo.latitude, lng: warehouseInfo.longitude },
            labelHeading: warehouseInfo.name,
          },
        ]
      : undefined;

    return (
      <YandexMap
        mapContainerClassName={mapContainerClassName}
        markers={yandexMarkers}
        center={mapCenter}
        mapId={mapId}
        zoom={zoom}
        withMarkerPopups={withMarkerPopups}
      />
    );
  }

  const googleMarkers = warehouseInfo
    ? [
        {
          id: warehouseInfo.warehouseId,
          position: { lat: warehouseInfo.latitude, lng: warehouseInfo.longitude },
        },
      ]
    : undefined;

  return (
    <Map
      mapContainerClassName={mapContainerClassName}
      markers={googleMarkers}
      center={mapCenter}
      mapId={mapId}
      zoom={zoom}
      withMarkerPopups={withMarkerPopups}
    />
  );
});
